import { useQuery } from "urql"
import { RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { CopyButton } from "@/components/shared/copy-button"
import { ActiveTunnelsQuery } from "@/lib/graphql/queries"
import type { ResultOf } from "@/lib/graphql/types"

type TunnelItem = ResultOf<typeof ActiveTunnelsQuery>["activeTunnels"][number]

const columns: Column<TunnelItem>[] = [
  {
    header: "Slug",
    cell: (row) => <code className="text-xs font-mono">{row.slug}</code>,
  },
  {
    header: "Source URL",
    cell: (row) => (
      <div className="flex items-center gap-1">
        <code className="text-xs font-mono truncate max-w-md">{row.sourceUrl}</code>
        <CopyButton text={row.sourceUrl} />
      </div>
    ),
  },
  {
    header: "Tunnel ID",
    cell: (row) => (
      <code className="text-xs font-mono text-muted-foreground">{row.id.slice(0, 8)}</code>
    ),
  },
  {
    header: "Status",
    cell: () => (
      <Badge variant="default" className="text-2xs">
        Connected
      </Badge>
    ),
  },
]

export function TunnelsPage() {
  const [result, reexecute] = useQuery({ query: ActiveTunnelsQuery })

  const tunnels = result.data?.activeTunnels ?? []

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tunnels"
        description="Active webhook tunnels forwarding to local development servers"
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => reexecute({ requestPolicy: "network-only" })}
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Refresh
        </Button>
      </PageHeader>

      <DataTable
        columns={columns}
        data={tunnels}
        keyFn={(row) => row.id}
        loading={result.fetching}
        emptyTitle="No active tunnels"
        emptyDescription="Start a tunnel with: hivehook tunnel <port>"
      />
    </div>
  )
}
